import React, { useId, useMemo } from 'react'

function clampPercent(percent) {
  const n = Number(percent)
  if (!Number.isFinite(n)) return 0
  if (n < 0) return 0
  if (n > 1) return 1
  return n
}

function hexToRgb(hex) {
  const h = String(hex || '').replace('#', '')
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h
  const n = parseInt(full, 16)
  if (!Number.isFinite(n)) return { r: 0, g: 0, b: 0 }
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

function rgbToHex({ r, g, b  }) {
  const to = (v) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

function mixRgb(a, b, t) {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  }
}

/** Interpolate across color stops (or match library first→last when 2+ colors). */
export function buildLevelColors(colors, nrOfLevels) {
  const stops = Array.isArray(colors) && colors.length > 0 ? colors : ['#00FF00']
  const n = Math.max(1, Math.floor(nrOfLevels) || 1)
  if (stops.length === n) return stops.slice()

  // Match react-gauge-chart: interpolate first → last only.
  const start = hexToRgb(stops[0])
  const end = hexToRgb(stops[stops.length - 1])
  const out = []
  for (let i = 0; i < n; i += 1) {
    const t = n === 1 ? 0 : i / (n - 1)
    out.push(rgbToHex(mixRgb(start, end, t)))
  }
  return out
}

function polar(cx, cy, r, theta) {
  // Library convention: 0 → left, π/2 → up, π → right
  return {
    x: cx - r * Math.cos(theta),
    y: cy - r * Math.sin(theta),
  }
}

function donutSegment(cx, cy, rOuter, rInner, a0, a1) {
  const p0 = polar(cx, cy, rOuter, a0)
  const p1 = polar(cx, cy, rOuter, a1)
  const p2 = polar(cx, cy, rInner, a1)
  const p3 = polar(cx, cy, rInner, a0)
  const large = a1 - a0 > Math.PI ? 1 : 0
  // Increasing theta is left→up→right = clockwise on screen → sweep 1
  return [
    `M ${p0.x} ${p0.y}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${p1.x} ${p1.y}`,
    `L ${p2.x} ${p2.y}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${p3.x} ${p3.y}`,
    'Z',
  ].join(' ')
}

function needlePath(cx, cy, length, baseRadius, percent) {
  const theta = clampPercent(percent) * Math.PI
  const bx = cx
  const by = cy - baseRadius / 2
  const tipX = bx - length * Math.cos(theta)
  const tipY = by - length * Math.sin(theta)
  const leftX = bx - baseRadius * Math.cos(theta - Math.PI / 2)
  const leftY = by - baseRadius * Math.sin(theta - Math.PI / 2)
  const rightX = bx - baseRadius * Math.cos(theta + Math.PI / 2)
  const rightY = by - baseRadius * Math.sin(theta + Math.PI / 2)
  return `M ${leftX} ${leftY} L ${tipX} ${tipY} L ${rightX} ${rightY} Z`
}

/**
 * Drop-in for `react-gauge-chart` covering props used in this app.
 * Pure SVG — no d3.
 */
export default function GaugeChart({
  id,
  percent = 0.4,
  nrOfLevels = 3,
  colors = ['#00FF00', '#FF0000'],
  arcWidth = 0.2,
  arcPadding = 0.05,
  hideText = false,
  needleColor = '#464A4F',
  needleBaseColor = '#464A4F',
  needleScale = 0.55,
  textColor = '#464A4F',
  formatTextValue,
  style,
  className,
  marginInPercent = 0.05,
}) {
  const reactId = useId()
  const svgId = id || `gauge-${reactId}`
  const p = clampPercent(percent)

  const geometry = useMemo(() => {
    const width = 250
    const height = 145
    const margin = width * marginInPercent
    const cx = width / 2
    const cy = height - margin * 0.35
    const rOuter = Math.min(cx - margin, cy - margin * 0.5)
    const rInner = rOuter * (1 - Math.min(0.95, Math.max(0.05, arcWidth)))
    const levels = buildLevelColors(colors, nrOfLevels)
    const n = levels.length
    const pad = Math.max(0, arcPadding)
    const totalPad = Math.max(0, n - 1) * pad
    const usable = Math.PI - totalPad
    const slice = usable / n

    const arcs = levels.map((color, i) => {
      const a0 = i * (slice + pad)
      const a1 = a0 + slice
      return {
        color,
        d: donutSegment(cx, cy, rOuter, rInner, a0, a1),
      }
    })

    const needleLen = rOuter * needleScale
    const needleBase = Math.max(4, 15 * (width / 500))

    return {
      width,
      height,
      cx,
      cy,
      arcs,
      needleLen,
      needleBase,
      rOuter,
    }
  }, [
    colors,
    nrOfLevels,
    arcWidth,
    arcPadding,
    marginInPercent,
    needleScale,
  ])

  const text =
    formatTextValue?.(Math.round(p * 10000) / 100) ??
    `${Math.round(p * 10000) / 100}%`

  return (
    <div
      id={svgId}
      className={className}
      style={{ width: '100%', ...style }}
    >
      <svg
        viewBox={`0 0 ${geometry.width} ${geometry.height}`}
        width="100%"
        role="img"
        aria-label={hideText ? `Gauge ${Math.round(p * 100)}%` : undefined}
      >
        <g>
          {geometry.arcs.map((arc, i) => (
            <path key={i} d={arc.d} fill={arc.color} />
          ))}
          <path
            d={needlePath(
              geometry.cx,
              geometry.cy,
              geometry.needleLen,
              geometry.needleBase,
              p,
            )}
            fill={needleColor}
          />
          <circle
            cx={geometry.cx}
            cy={geometry.cy - geometry.needleBase / 2}
            r={geometry.needleBase}
            fill={needleBaseColor}
          />
          {!hideText && (
            <text
              x={geometry.cx}
              y={geometry.cy - geometry.rOuter * 0.25}
              textAnchor="middle"
              fill={textColor}
              fontSize={geometry.width / 11}
            >
              {text}
            </text>
          )}
        </g>
      </svg>
    </div>
  )
}
